/**
 * O que o "Mapear para" pode oferecer a UM campo — funcoes puras.
 *
 * Tres regras que so existiam no servidor (ou em lugar nenhum) e que o gestor
 * so descobria depois de publicar:
 *
 *   - FORM-23: dois campos no mesmo destino. Na pagina hospedada o ultimo
 *     vencia e o outro sumia sem aviso ("Telefone" e "WhatsApp" perdendo um
 *     numero); na ingestao externa o formulario inteiro passava a recusar
 *     100% dos envios. So Observacoes e Bio aceitam mais de um campo — la o
 *     servidor concatena "Rotulo: valor", uma linha por campo.
 *   - FORM-03: "Valor estimado" numa lista de faixas ("Ate R$ 20 mil") nunca
 *     vira dinheiro. O destino vale so para texto e numero, e o servidor
 *     recusa o `Salvar campos` inteiro quando o tipo nao bate.
 *   - FORM-22: campo de e-mail e de telefone nascem sem destino, e o lead
 *     chegava ao CRM sem ninguem para chamar. Aqui fica a sugestao por tipo.
 */
import {
  DESTINOS_DE_TEXTO_LIVRE,
  DESTINO_VALOR_ESTIMADO,
  MAPEIA_PARA_GRUPOS,
  MAPEIA_PARA_LABEL,
  TIPOS_COM_VALOR_ESTIMADO,
} from "../../services/formularios";

/** Destino que o tipo do campo sugere quando ele ainda esta livre (FORM-22). */
export const DESTINO_SUGERIDO_POR_TIPO = {
  EMAIL: "contato_email",
  TELEFONE: "contato_telefone",
};

export const DESTINO_TELEFONE = "contato_telefone";
export const DESTINO_TELEFONE_SECUNDARIO = "telefone_secundario";

/** Observacoes e Bio aceitam varios campos; os demais destinos, nao. */
export function aceitaVariosCampos(destino) {
  return DESTINOS_DE_TEXTO_LIVRE.includes(destino);
}

/** O destino cabe neste tipo de campo? (hoje so o dinheiro restringe). */
export function destinoCabeNoTipo(destino, tipo) {
  if (destino !== DESTINO_VALOR_ESTIMADO) return true;
  return TIPOS_COM_VALOR_ESTIMADO.includes(tipo);
}

/**
 * Destino sugerido para um campo NOVO deste tipo, ou "" quando nao ha
 * sugestao ou o destino ja e de outro campo. `destinosUsados` e
 * `{destino: "Rotulo do campo que usa"}`.
 */
export function sugerirDestino(tipo, destinosUsados = {}) {
  const destino = DESTINO_SUGERIDO_POR_TIPO[tipo];
  if (!destino || destinosUsados[destino]) return "";
  return destino;
}

/**
 * Grupos do seletor com o que cada opcao tem de especial:
 *   - `ocupadoPor`: rotulo do campo que ja usa o destino ("" quando livre);
 *   - `desabilitada`: nao da para escolher (ocupado por outro campo).
 * O destino ATUAL do campo nunca e escondido nem desabilitado — some-lo
 * trocaria o mapeamento gravado sem ninguem pedir.
 */
export function gruposDeMapeamento(tipo, { destinosUsados = {}, atual = "" } = {}) {
  return MAPEIA_PARA_GRUPOS.map((grupo) => ({
    grupo: grupo.grupo,
    opcoes: grupo.opcoes
      .filter((o) => o.value === atual || destinoCabeNoTipo(o.value, tipo))
      .map((o) => {
        const ocupadoPor =
          o.value !== atual && !aceitaVariosCampos(o.value)
            ? destinosUsados[o.value] || ""
            : "";
        return {
          value: o.value,
          label: ocupadoPor ? `${o.label} — usado por “${ocupadoPor}”` : o.label,
          ocupadoPor,
          desabilitada: Boolean(ocupadoPor),
        };
      }),
  })).filter((grupo) => grupo.opcoes.length > 0);
}

/**
 * Dica quando o destino natural do tipo ja e de outro campo: um formulario
 * com dois telefones e legitimo, e o segundo numero tem destino proprio.
 */
export function ajudaDoDestinoOcupado(tipo, destinosUsados = {}, atual = "") {
  if (tipo !== "TELEFONE" || atual === DESTINO_TELEFONE) return "";
  const ocupante = destinosUsados[DESTINO_TELEFONE];
  if (!ocupante || atual === DESTINO_TELEFONE_SECUNDARIO) return "";
  return (
    `“${MAPEIA_PARA_LABEL[DESTINO_TELEFONE]}” já é de “${ocupante}”. ` +
    `Para um segundo número use “${MAPEIA_PARA_LABEL[DESTINO_TELEFONE_SECUNDARIO]}”.`
  );
}

/**
 * Aviso do destino ja GRAVADO que o tipo atual nao aceita (formulario antigo
 * ou troca de tipo): sem ele o gestor so descobre no "Salvar campos", que
 * recusa o formulario inteiro.
 */
export function avisoDoDestinoGravado(tipo, atual = "") {
  if (!atual || destinoCabeNoTipo(atual, tipo)) return "";
  return (
    `“${MAPEIA_PARA_LABEL[atual] || atual}” só aceita campo de texto ou número. ` +
    "Troque o destino ou o tipo do campo — o servidor recusa o Salvar campos assim."
  );
}
